<template>
  <div>
    <div>Target copies on {{ target }}: {{ copies.text }}</div>
    <div class="mb-3 grey--text text--darken-2">
      Router: {{ target }} has {{ hostPending.target }} queued events . {{ preflight.sourceHostname }} has {{ hostPending.source }}
    </div>

    <step-button
      name="canBuild"
      :label="`Build ${missing.length} missing`"
      :steps="preflight.steps"
      :busy="busy"
      :loading="loadingKey === 'build'"
      @click="askBuild"
    />
    <step-button
      name="canVerify"
      label="Verify target"
      :steps="preflight.steps"
      :busy="busy"
      :loading="verifying"
      @click="verify"
    />
    <step-button
      :override="driftState"
      :label="`Fix drift (${plan.count})`"
      color="secondary"
      :busy="busy"
      :loading="loadingKey === 'drift'"
      @click="askDrift"
    />
    <step-button
      name="canRemoveTargetCopies"
      :label="removal.label"
      :steps="preflight.steps"
      color="secondary"
      :busy="busy"
      :loading="loadingKey === 'orphans'"
      @click="askOrphans"
    />

    <div v-if="verifying && progress" class="mt-3">
      <v-progress-linear :value="progressPercent" height="18" />
      <div class="mt-1">
        {{ progressText }}
        <v-btn x-small text color="primary" :disabled="stopRequested" @click="stopRequested = true">Stop after this chunk</v-btn>
      </div>
    </div>

    <v-alert v-if="lastBatch" :type="lastBatch.type" dense outlined class="mt-3 mb-0">
      {{ lastBatch.text }}
      <ul v-if="lastBatch.rows.length" class="mb-0">
        <li v-for="(r, i) in lastBatch.rows" :key="`row-${i}`">
          <strong>{{ r.avcId }}</strong> {{ r.outcome }}: {{ r.message }}
        </li>
      </ul>
    </v-alert>
    <v-alert v-if="notice" type="success" dense outlined class="mt-3 mb-0">{{ notice }}</v-alert>

    <v-alert v-if="alert" :type="alert.type" dense outlined class="mt-3 mb-0">
      {{ alert.text }}
      <v-btn v-if="alert.type === 'error'" small text class="ml-2" @click="retryLast">Retry</v-btn>
    </v-alert>

    <div v-if="lastVerify" class="mt-3">
      <div>
        Result {{ verifiedClock }}:
        <v-chip small :color="verifyReady ? 'success' : 'warning'" :dark="verifyReady" class="ml-1 mr-2">
          {{ verifyReady ? "READY" : "NOT READY" }}
        </v-chip>
        present {{ lastVerify.avcsPresent }}/{{ lastVerify.avcsChecked }} . match {{ matchCount }} .
        mismatch {{ lastVerify.avcsMismatched }} . missing {{ lastVerify.avcsMissing }}
      </div>
      <div class="mt-1">
        <span v-if="lastVerify.cvcInterface" class="mr-6">{{ subifText("CVC subif", lastVerify.cvcInterface) }}</span>
        <span v-if="lastVerify.loopInterface">{{ subifText("Loop subif", lastVerify.loopInterface) }}</span>
      </div>
      <div v-if="mismatches.length" class="mt-2">
        <div class="font-weight-medium">Mismatches</div>
        <div v-for="d in mismatches" :key="`mismatch-${d.avcId}`" class="d-flex align-center">
          <span class="mr-3">{{ d.avcId }} {{ (d.issues || [])[0] || "mismatch" }}</span>
          <step-button
            :override="fixState(d)"
            :label="fixLabel(d)"
            :busy="busy"
            :loading="loadingKey === `fix-${d.avcId}`"
            outlined
            @click="fixOne(d)"
          />
        </div>
      </div>
      <div v-if="(lastVerify.unexpected || []).length" class="mt-2">
        <div class="font-weight-medium">Unexpected</div>
        <div v-for="u in lastVerify.unexpected" :key="`unexpected-${u.copyId}`" class="d-flex align-center">
          <span class="mr-3">copy {{ u.copyId }} {{ u.avcId }} ({{ u.reason }})</span>
          <step-button
            name="canRemoveTargetCopies"
            label="Remove"
            :steps="preflight.steps"
            :busy="busy"
            :loading="loadingKey === `remove-${u.copyId}`"
            outlined
            @click="removeOne(u)"
          />
        </div>
      </div>
    </div>

    <confirm-action
      :dialog="confirm.open"
      :text="confirm.text"
      :proceed-text="confirm.proceedText"
      cancel-text="Cancel"
      @onBtnClick="onConfirm"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { formatClockInZone, SYDNEY } from "@/utils/zonedTime";
import {
  copiesSummary,
  driftButton,
  driftKind,
  fixButton,
  orphanRemoval,
  planDriftFixes,
  missingBuildIds,
  verifyPlan,
  runVerifyLoop,
  batchSummary,
} from "@/utils/cvcMigrationPage";
import StepButton from "./step-button";
import SectionActions from "./section-actions";

// Section B: build the copies on the target, verify them, fix what drifted.
// Verify runs as sequential calls of a few AVCs each (a router session is
// slow) and shows its own progress instead of the page-wide loader.
export default {
  name: "CvcMigrationPrebuild",
  components: { StepButton },
  mixins: [SectionActions],
  props: {
    preflight: { type: Object, required: true },
    cvcId: { type: Number, required: true },
    targetHostId: { type: Number, required: true },
    // The merged answer of the last verify, kept by the page.
    lastVerify: { type: Object, default: null },
    verifiedAt: { type: Date, default: null },
  },
  data: () => ({
    verifying: false,
    progress: null,
    stopRequested: false,
    lastBatch: null,
    notice: "",
  }),
  computed: {
    rows () {
      return this.preflight.rows || [];
    },
    record () {
      return this.preflight.record;
    },
    target () {
      return this.preflight.targetHostname;
    },
    hostPending () {
      const pending = this.preflight.hostPendingEvents || {};
      return { source: pending.source || 0, target: pending.target || 0 };
    },
    copies () {
      return copiesSummary(this.record, this.rows);
    },
    missing () {
      return missingBuildIds(this.rows);
    },
    plan () {
      return planDriftFixes(this.rows, this.lastVerify, this.record);
    },
    removal () {
      return orphanRemoval(this.record, this.plan, this.target);
    },
    driftState () {
      return driftButton(this.plan, this.preflight.steps);
    },
    progressPercent () {
      return this.progress ? ((this.progress.index - 1) / this.progress.total) * 100 : 0;
    },
    progressText () {
      const p = this.progress;
      const what = p.first > 0 ? `AVCs ${p.first}-${p.last}` : "CVC and loop subinterfaces";
      return `Verifying ${p.index}/${p.total} (${what})`;
    },
    verifyReady () {
      const v = this.lastVerify;
      return v.targetReady === null || v.targetReady === undefined ? !!v.ready : !!v.targetReady;
    },
    verifiedClock () {
      return this.verifiedAt ? `${formatClockInZone(this.verifiedAt, SYDNEY)} SYD` : "";
    },
    matchCount () {
      return (this.lastVerify.details || []).filter(d => d.match).length;
    },
    mismatches () {
      return (this.lastVerify.details || []).filter(d => !d.match);
    },
  },
  methods: {
    ...mapActions("cvcMigration", [
      "cvcMigrationBuild",
      "cvcMigrationRepolicy",
      "cvcMigrationVerifyTargetChunk",
      "cvcMigrationRemoveTargetCopies",
    ]),
    subifText (name, part) {
      return part.present ? `${name} ${part.interface} present, ${part.status}` : `${name} ${part.interface} MISSING`;
    },
    fixLabel (detail) {
      return driftKind(detail) === "policy" ? "Repolicy" : "Rebuild";
    },
    fixState (detail) {
      return fixButton(this.preflight.steps, detail);
    },
    askBuild () {
      const n = this.missing.length;
      this.ask(`Queue ${n} builds on ${this.target}? ${this.preflight.sourceHostname} stays live.`, "Queue builds", () => this.run("build", async () => {
        const batch = await this.cvcMigrationBuild({ cvcId: this.cvcId, targetHostId: this.targetHostId });
        this.lastBatch = batchSummary("Build", batch, new Date(), this.preflight.poiState);
      }));
    },
    verify () {
      return this.run("verify", async () => {
        this.stopRequested = false;
        this.verifying = true;
        this.notice = "";
        try {
          const out = await runVerifyLoop({
            plan: verifyPlan(this.rows, this.preflight.verifyMaxAvcs),
            callChunk: avcIds => this.cvcMigrationVerifyTargetChunk({
              cvcId: this.cvcId,
              targetHostId: this.targetHostId,
              avcIds,
            }),
            targetHostname: this.target,
            shouldStop: () => this.stopRequested,
            onProgress: p => { this.progress = p; },
          });
          if (out.results.length) this.$emit("verified", { merged: out.merged, partial: false });
          if (out.stoppedBy === "busy") this.alert = { type: "warning", text: out.message };
          if (out.stoppedBy === "refused") this.alert = { type: "warning", text: out.message };
          if (out.stoppedBy === "error") this.alert = { type: "error", text: out.message };
        } finally {
          this.verifying = false;
          this.progress = null;
        }
      });
    },
    askDrift () {
      const text = `Fix drift: ${this.plan.lines.join("; ")}. Runs in this order and stops at the first failure.`;
      this.ask(text, "Fix drift", () => this.run("drift", () => this.fixDrift()));
    },
    async fixDrift () {
      const plan = this.plan;
      const ids = plan.build.concat(plan.rebuild);
      const stage = async (name, call) => {
        try {
          return await call();
        } catch (e) {
          e.stage = name;
          throw e;
        }
      };
      if (ids.length) {
        await stage("Build", () => this.cvcMigrationBuild({ cvcId: this.cvcId, targetHostId: this.targetHostId, avcIds: ids }));
      }
      if (plan.repolicy.length) {
        await stage("Repolicy", () => this.cvcMigrationRepolicy({ cvcId: this.cvcId, avcIds: plan.repolicy }));
      }
      if (plan.remove.length) {
        await stage("Remove copies", () => this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: plan.remove }));
      }
      this.notice = `Queued: ${ids.length} builds, ${plan.repolicy.length} repolicies, ${plan.remove.length} removals. Wait for the queued changes, then Verify target.`;
    },
    askOrphans () {
      const removal = this.removal;
      this.ask(removal.question, "Remove", () => this.run("orphans", async () => {
        await this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: removal.ids });
        this.notice = removal.queued;
      }));
    },
    fixOne (detail) {
      this.run(`fix-${detail.avcId}`, async () => {
        if (driftKind(detail) === "policy") {
          await this.cvcMigrationRepolicy({ cvcId: this.cvcId, avcIds: [detail.avcId] });
          this.notice = `Repolicy of ${detail.avcId} queued. Wait for the queued change, then Verify target.`;
        } else {
          await this.cvcMigrationBuild({ cvcId: this.cvcId, targetHostId: this.targetHostId, avcIds: [detail.avcId] });
          this.notice = `Rebuild of ${detail.avcId} queued. Wait for the queued change, then Verify target.`;
        }
      });
    },
    removeOne (unexpected) {
      this.run(`remove-${unexpected.copyId}`, async () => {
        await this.cvcMigrationRemoveTargetCopies({ cvcId: this.cvcId, copyIds: [unexpected.copyId] });
        this.notice = `Removal of copy ${unexpected.copyId} queued. Wait for the queued change, then Verify target.`;
      });
    },
  },
};
</script>
